import React, { useEffect, useMemo, useState } from 'react';
import { salApi } from '../../lib/api';
import { formatCurrency, formatPercentage } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import {
  ChevronRight, ChevronDown, Building2, HardHat, Zap, Droplets, Layers,
  Package, Plus, Scissors, Search, AlertTriangle, CheckCircle2, Clock, Circle,
} from 'lucide-react';

const CAT_ICON = { C: HardHat, D: Zap, E: Droplets, other: Layers, historical: AlertTriangle };
const CAT_ACCENT = { C: '#FF7819', D: '#0078C8', E: '#00A676', other: '#737373', historical: '#B45309' };
const CAT_LABEL = { C: 'Opere edili', D: 'Impianti elettrici', E: 'Idraulici, termici e clima', other: 'Altre lavorazioni contrattuali' };

const STATUS_META = {
  non_iniziata: { label: 'Non iniziata', cls: 'bg-[#F5F5F5] text-[#737373]', Icon: Circle },
  in_corso: { label: 'In corso', cls: 'bg-[#FFF0E5] text-[#FF7819]', Icon: Clock },
  completata: { label: 'Completata', cls: 'bg-[#E6F7F0] text-[#00A676]', Icon: CheckCircle2 },
  stralciata: { label: 'Stralciata', cls: 'bg-red-50 text-red-600', Icon: Scissors },
};

const money = (v) => formatCurrency(v || 0);
const pct = (v) => formatPercentage(v || 0, 1);
const voci = (n) => `${n || 0} ${(n === 1) ? 'voce' : 'voci'}`;

const KpiBar = ({ value, color = '#FF7819' }) => (
  <div className="h-2 w-full rounded-full bg-[#F0F0F0] overflow-hidden mt-2">
    <div className="h-full rounded-full transition-[width] duration-500"
      style={{ width: `${Math.min(value || 0, 100)}%`, backgroundColor: color }} />
  </div>
);

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.non_iniziata;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

const ItemRow = ({ it }) => (
  <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-[13px] border-t border-[#F0F0F0] hover:bg-[#FAFAFA]"
    data-testid={`pa-item-${it.id}`}>
    <div className="col-span-12 md:col-span-4 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-[#F5F5F5] text-[#525252]">{it.code}</span>
        {it.canonical_code && it.canonical_code !== it.code && (
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#FFF0E5] text-[#FF7819] font-medium"
            title={it.canonical_subcategory_label || ''}>→ {it.canonical_code}</span>
        )}
        {it.badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#B45309]">{it.badge}</span>}
        {it.source === 'extra' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#6D28D9]">EXTRA</span>}
        {it.manual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E0F2FE] text-[#0369A1] font-medium" data-testid={`pa-badge-manual-${it.id}`}>MANUALE</span>}
      </div>
      <p className="text-[12px] text-[#525252] truncate mt-0.5" title={it.description}>{it.description}</p>
    </div>
    <div className="col-span-4 md:col-span-2 text-right text-[12px] text-[#737373]">
      <span className="text-[#171717] font-medium">{(it.executed_quantity ?? 0)}</span> / {it.current_quantity ?? 0} {it.unit}
    </div>
    <div className="col-span-4 md:col-span-2 text-right">
      <span className="text-[#171717] font-medium">{money(it.executed_value)}</span>
      <p className="text-[11px] text-[#a3a3a3]">/ {money(it.contract_value)}</p>
    </div>
    <div className="col-span-2 md:col-span-2 text-right font-semibold text-[#FF7819]">{pct(it.completion)}</div>
    <div className="col-span-2 md:col-span-2 flex md:justify-end"><StatusBadge status={it.status} /></div>
  </div>
);

const ItemTable = ({ items, groupKey }) => (
  <div className="rounded-md overflow-hidden border border-[#F0F0F0] mt-1 bg-white">
    <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-1.5 bg-[#F7F7F7] text-[10px] font-semibold uppercase tracking-wide text-[#a3a3a3]">
      <div className="col-span-4">Codice / Descrizione</div>
      <div className="col-span-2 text-right">Q.tà eseg. / att.</div>
      <div className="col-span-2 text-right">Val. eseg. / att.</div>
      <div className="col-span-2 text-right">%</div>
      <div className="col-span-2 text-right">Stato</div>
    </div>
    {items.map((it) => <ItemRow key={`${groupKey}-${it.id}`} it={it} />)}
  </div>
);

const GroupBlock = ({ group, accent, filterItems, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const subcats = (group.subcategories || [])
    .map((sc) => ({ ...sc, items: filterItems(sc.items || []) }))
    .filter((sc) => sc.items.length > 0);
  const visibleCount = subcats.reduce((n, sc) => n + sc.items.length, 0);
  if (group.item_count === 0) return null;
  const isEmptyAfterFilter = visibleCount === 0;
  const isNA = group.key === '__nonassigned__';
  return (
    <div className={`rounded-lg border ${isNA ? 'border-dashed border-[#D4D4D4] bg-[#FAFAFA]' : 'border-[#E5E5E5] bg-white'}`} data-testid={`pa-group-${group.key}`}>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left"
        data-testid={`pa-group-toggle-${group.key}`}>
        <div className="flex items-center gap-2.5 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3] shrink-0" />}
          {isNA ? <Circle className="w-4 h-4 shrink-0 text-[#a3a3a3]" /> : <Building2 className="w-4 h-4 shrink-0" style={{ color: accent }} />}
          <span className={`text-[14px] truncate ${isNA ? 'italic text-[#737373] font-medium' : 'font-bold text-[#171717]'}`}>{group.name}</span>
          <Badge variant="outline" className="text-[10px] shrink-0">{voci(group.item_count)}</Badge>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[12px] font-medium text-[#171717] hidden sm:inline">{money(group.executed)} <span className="text-[#a3a3a3]">/ {money(group.contract)}</span></span>
          <span className="text-[14px] font-bold w-14 text-right" style={{ color: isNA ? '#737373' : accent }}>{pct(group.percentage)}</span>
        </div>
      </button>
      {open && (
        <div className="px-2 pb-2">
          {isEmptyAfterFilter ? (
            <p className="text-[12px] text-[#a3a3a3] px-3 py-2">Nessuna voce con i filtri attivi</p>
          ) : subcats.map((sc) => (
            <div key={sc.code || 'none'} className="mt-1 ml-3 border-l-2 border-[#F0F0F0] pl-2">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#a3a3a3]">{sc.label}</span>
                <span className="text-[11px] font-medium text-[#737373]">{pct(sc.percentage)} · {money(sc.executed)}/{money(sc.contract)}</span>
              </div>
              <ItemTable items={sc.items} groupKey={group.key} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CategoryAccordion = ({ code, cat, filterItems, groupVisible }) => {
  const [open, setOpen] = useState(false);
  const accent = CAT_ACCENT[code] || '#737373';
  const Icon = CAT_ICON[code] || Layers;
  const label = `${code === 'other' ? '' : code + ' · '}${CAT_LABEL[code] || (cat && (cat.short || cat.label)) || code}`;
  const empty = !cat || cat.item_count === 0;

  if (empty) {
    return (
      <div className="rounded-lg border border-[#F0F0F0] bg-[#FCFCFC] px-4 py-2.5 flex items-center gap-2.5" data-testid={`pa-cat-${code}`}>
        <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />
        <Icon className="w-4 h-4" style={{ color: '#C4C4C4' }} />
        <span className="text-[13px] font-medium text-[#a3a3a3]">{label}</span>
        <span className="ml-auto text-[12px] text-[#c4c4c4] italic">Nessuna lavorazione</span>
      </div>
    );
  }

  const groups = [...(cat.subcontractors || []), cat.nonassigned]
    .filter((g) => g && g.item_count > 0 && (!groupVisible || groupVisible(g)));

  return (
    <div className="rounded-lg border border-[#E5E5E5] bg-white shadow-sm overflow-hidden" data-testid={`pa-cat-${code}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA]"
        data-testid={`pa-cat-toggle-${code}`}>
        {open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3] shrink-0" />}
        <Icon className="w-4 h-4 shrink-0" style={{ color: accent }} />
        <span className="font-semibold text-[14px] text-[#171717] truncate">{label}</span>
        <span className="text-[14px] font-bold ml-3 shrink-0" style={{ color: accent }}>{pct(cat.percentage)}</span>
        <span className="hidden md:inline text-[12px] text-[#737373] ml-auto shrink-0">{money(cat.executed)} / {money(cat.contract)}</span>
        <span className="hidden lg:inline text-[12px] text-[#a3a3a3] ml-3 shrink-0">Residuo {money(cat.residual)}</span>
        <span className="text-[12px] text-[#737373] ml-3 shrink-0 md:ml-4">{cat.completed_count}/{cat.item_count}</span>
      </button>
      {open && (
        <div className="p-3 space-y-2 bg-[#FCFCFC] border-t border-[#F0F0F0]">
          {groups.length === 0 ? (
            <p className="text-[12px] text-[#a3a3a3] px-2 py-1">Nessun gruppo con i filtri attivi</p>
          ) : groups.map((g) => (
            <GroupBlock key={g.key} group={g} accent={accent} filterItems={filterItems}
              defaultOpen={groups.length === 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const CdeCard = ({ code, cat }) => {
  const accent = CAT_ACCENT[code];
  const Icon = CAT_ICON[code] || Layers;
  const label = `${code} · ${CAT_LABEL[code]}`;
  const empty = !cat || cat.item_count === 0;
  return (
    <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex flex-col" data-testid={`pa-card-${code}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <p className="text-[13px] font-semibold text-[#171717]">{label}</p>
        <span className="ml-auto text-lg font-bold" style={{ color: empty ? '#C4C4C4' : accent }} data-testid={`pa-card-pct-${code}`}>{empty ? '—' : pct(cat.percentage)}</span>
      </div>
      <KpiBar value={empty ? 0 : cat.percentage} color={accent} />
      {empty ? (
        <p className="text-[12px] text-[#a3a3a3] italic mt-3">Nessuna lavorazione</p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-y-1 text-[12px]">
          <span className="text-[#737373]">Eseguito</span><span className="text-right font-medium text-[#171717]">{money(cat.executed)}</span>
          <span className="text-[#737373]">Contrattuale</span><span className="text-right font-medium text-[#171717]">{money(cat.contract)}</span>
          <span className="text-[#737373]">Residuo</span><span className="text-right font-medium text-[#171717]">{money(cat.residual)}</span>
          <span className="text-[#737373]">Lavorazioni</span><span className="text-right font-medium text-[#171717]">{cat.completed_count}/{cat.item_count}</span>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ title, color, pctVal, Icon, testid, rows, count }) => (
  <div className="rounded-xl border border-[#EDEDED] bg-white shadow-sm p-4 flex flex-col" data-testid={testid}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" style={{ color }} />
      <p className="text-[13px] font-semibold text-[#171717]">{title}</p>
      {typeof pctVal === 'number' && <span className="ml-auto text-lg font-bold" style={{ color }}>{pct(pctVal)}</span>}
    </div>
    <KpiBar value={pctVal || 0} color={color} />
    <div className="mt-3 grid grid-cols-2 gap-y-1 text-[12px]">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}><span className="text-[#737373]">{k}</span><span className="text-right font-medium text-[#171717]">{v}</span></React.Fragment>
      ))}
    </div>
    <p className="text-[11px] text-[#a3a3a3] mt-auto pt-2">{voci(count)}</p>
  </div>
);

const ExtraSection = ({ extra, canViewEconomic }) => {
  const [open, setOpen] = useState(false);
  const has = extra.item_count > 0;
  return (
    <div className="rounded-lg border border-[#E9D5FF] bg-white shadow-sm overflow-hidden" data-testid="pa-section-extra">
      <button onClick={() => has && setOpen((o) => !o)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${has ? 'hover:bg-[#FAF5FF]' : 'cursor-default'}`} data-testid="pa-section-toggle-extra">
        {has ? (open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3]" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3]" />) : <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />}
        <Plus className="w-4 h-4 shrink-0 text-[#6D28D9]" />
        <span className="font-semibold text-[14px] text-[#171717]">Extra lavori</span>
        <Badge variant="outline" className="text-[10px]">{extra.item_count}</Badge>
        <span className="text-[14px] font-bold ml-3 text-[#6D28D9]">{pct(extra.percentage)}</span>
        {canViewEconomic && <span className="hidden md:inline text-[12px] text-[#737373] ml-auto">Attivo {money(extra.executed)} / {money(extra.contract)}</span>}
      </button>
      {canViewEconomic && (
        <div className="px-4 pb-2 grid grid-cols-3 gap-2 text-[12px] border-t border-[#F0F0F0] pt-2" data-testid="pa-extra-metrics">
          <div><p className="text-[#a3a3a3]">Previsto (attivi)</p><p className="font-semibold text-[#171717]">{money(extra.contract)}</p></div>
          <div><p className="text-[#a3a3a3]">Eseguito</p><p className="font-semibold text-[#171717]">{money(extra.executed)}</p></div>
          <div><p className="text-[#a3a3a3]">Avanzamento</p><p className="font-semibold text-[#6D28D9]">{pct(extra.percentage)}</p></div>
        </div>
      )}
      {extra.contract_all > extra.contract && (
        <p className="px-4 pb-2 text-[11px] text-[#a3a3a3]">Include {money(extra.contract_all - extra.contract)} di extra non ancora attivi (esclusi dall'avanzamento).</p>
      )}
      {open && has && (
        <div className="px-3 pb-3 bg-[#FCFCFC] border-t border-[#F0F0F0] pt-2">
          <ItemTable items={extra.items} groupKey="extra" />
        </div>
      )}
    </div>
  );
};

const SuppliesSection = ({ supplies, canViewEconomic }) => {
  const [open, setOpen] = useState(false);
  const has = supplies.item_count > 0;
  return (
    <div className="rounded-lg border border-[#BAE6FD] bg-white shadow-sm overflow-hidden" data-testid="pa-section-supplies">
      <button onClick={() => has && setOpen((o) => !o)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${has ? 'hover:bg-[#F0F9FF]' : 'cursor-default'}`} data-testid="pa-section-toggle-supplies">
        {has ? (open ? <ChevronDown className="w-4 h-4 text-[#a3a3a3]" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3]" />) : <ChevronRight className="w-4 h-4 text-[#D4D4D4]" />}
        <Package className="w-4 h-4 shrink-0 text-[#0078C8]" />
        <span className="font-semibold text-[14px] text-[#171717]">Forniture</span>
        <Badge variant="outline" className="text-[10px]">{supplies.item_count}</Badge>
        <span className="text-[14px] font-bold ml-3 text-[#0078C8]" title="Avanzamento logistico (consegnato/previsto)">{pct(supplies.percentage)}</span>
        {canViewEconomic && <span className="hidden md:inline text-[12px] text-[#737373] ml-auto">Consegnato {money(supplies.delivered_amount)} / {money(supplies.total_amount)}</span>}
      </button>
      {canViewEconomic && (
        <div className="px-4 pb-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px] border-t border-[#F0F0F0] pt-2" data-testid="pa-supplies-metrics">
          <div><p className="text-[#a3a3a3]">Previsto</p><p className="font-semibold text-[#171717]">{money(supplies.total_amount)}</p></div>
          <div><p className="text-[#a3a3a3]">Ordinato</p><p className="font-semibold text-[#171717]">{money(supplies.ordered_amount)}</p></div>
          <div><p className="text-[#a3a3a3]">Consegnato</p><p className="font-semibold text-[#171717]">{money(supplies.delivered_amount)}</p></div>
          <div><p className="text-[#a3a3a3]">Avanz. logistico</p><p className="font-semibold text-[#0078C8]">{pct(supplies.percentage)}</p></div>
        </div>
      )}
      <p className="px-4 pb-2 text-[11px] text-[#a3a3a3]">Logistica materiali · separata dal SAL lavori C/D/E.</p>
      {open && has && (
        <div className="px-3 pb-3 bg-[#FCFCFC] border-t border-[#F0F0F0] pt-2">
          <ItemTable items={supplies.items} groupKey="supplies" />
        </div>
      )}
    </div>
  );
};

const StoricoAlert = ({ historical, canViewEconomic }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] overflow-hidden" data-testid="pa-historical-block">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left" data-testid="pa-historical-toggle">
        <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0" />
        <span className="text-[13px] font-semibold text-[#92400E]">
          {historical.item_count} {historical.item_count === 1 ? 'voce da classificare' : 'voci da classificare'}
          {canViewEconomic && <span className="font-normal"> · {money(historical.contract)}</span>}
        </span>
        <Badge className="text-[9px] bg-[#FEF3C7] text-[#B45309] border-0 ml-1" data-testid="pa-badge-storico">STORICO</Badge>
        <Badge className="text-[9px] bg-[#FDE68A] text-[#92400E] border-0" data-testid="pa-badge-daclassificare">DA CLASSIFICARE</Badge>
        {open ? <ChevronDown className="w-4 h-4 text-[#B45309] ml-auto" /> : <ChevronRight className="w-4 h-4 text-[#B45309] ml-auto" />}
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-[#FDE68A] pt-2">
          {canViewEconomic && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[12px] mb-2" data-testid="pa-historical-metrics">
              <div><p className="text-[#a3a3a3]">Voci</p><p className="font-semibold text-[#171717]">{historical.item_count}</p></div>
              <div><p className="text-[#a3a3a3]">Contrattuale</p><p className="font-semibold text-[#171717]">{money(historical.contract)}</p></div>
              <div><p className="text-[#a3a3a3]">Eseguito</p><p className="font-semibold text-[#171717]">{money(historical.executed)}</p></div>
              <div><p className="text-[#a3a3a3]">Residuo</p><p className="font-semibold text-[#171717]">{money(historical.residual)}</p></div>
              <div><p className="text-[#a3a3a3]">Avanzamento</p><p className="font-semibold text-[#B45309]">{pct(historical.percentage)}</p></div>
            </div>
          )}
          <p className="text-[11px] text-[#B45309] mb-2">Voci non riconducibili a una voce canonica attiva. Nessuna mutazione: codici, prezzi e quantità restano invariati.</p>
          <ItemTable items={(historical.items || [])} groupKey="historical" />
        </div>
      )}
    </div>
  );
};

export const ProgressAnalyticsDashboard = ({ projectId, canViewEconomic = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fStatus, setFStatus] = useState('all');
  const [fCategory, setFCategory] = useState('all');
  const [fSub, setFSub] = useState('all');
  const [fAssign, setFAssign] = useState('all');
  const [search, setSearch] = useState('');
  const [showStricken, setShowStricken] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    salApi.getProgressAnalytics(projectId)
      .then((res) => { if (active) { setData(res.data); setError(null); } })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || 'Errore caricamento analytics'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId]);

  const filterItems = useMemo(() => (items) => {
    const q = search.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (fStatus !== 'all' && it.status !== fStatus) return false;
      if (q && !(`${it.code} ${it.description}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [fStatus, search]);

  if (loading) return <div className="rounded-xl border border-[#EDEDED] bg-white p-8 text-center text-[#a3a3a3]" data-testid="pa-loading">Caricamento riepilogo…</div>;
  if (error) return <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="pa-error">{error}</div>;
  if (!data) return null;

  const o = data.official_sal;
  const byCode = Object.fromEntries(data.categories.map((c) => [c.code, c]));
  const C = byCode.C, D = byCode.D, E = byCode.E;
  const other = byCode.other;
  const historical = byCode.historical;
  const recon = data.reconciliation;
  const reconOk = Math.abs(recon.CONTRACT_VALUE_RECONCILIATION_DELTA) < 0.01 && Math.abs(recon.EXECUTED_VALUE_RECONCILIATION_DELTA) < 0.01;

  const subOptions = Array.from(new Set(
    data.categories.flatMap((c) => (c.subcontractors || []).map((g) => g.name))
  )).sort();

  const groupVisible = (g) => {
    const isNA = g.key === '__nonassigned__';
    if (fAssign === 'assigned' && isNA) return false;
    if (fAssign === 'nonassigned' && !isNA) return false;
    if (fSub !== 'all' && g.name !== fSub && !(fSub === '__nonassigned__' && isNA)) return false;
    return true;
  };

  const catFilter = (code) => fCategory === 'all' || fCategory === code;
  const detailProps = { filterItems, groupVisible };

  return (
    <div className="space-y-3" data-testid="progress-analytics-dashboard">
      {/* SAL Principale — KPI dominante */}
      <div className="rounded-xl border border-[#FFE0CC] bg-gradient-to-r from-[#FFF8F3] to-white shadow-sm p-5" data-testid="pa-sal-total">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-wide text-[#737373]">SAL Principale · Lavori contrattuali</p>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-4xl font-bold text-[#FF7819]" data-testid="pa-sal-total-pct">{formatPercentage(o.principal_percentage || 0, 2)}</span>
              {canViewEconomic && (
                <span className="text-[14px] text-[#525252]">{money(o.principal_executed)} <span className="text-[#a3a3a3]">/ {money(o.principal_contract)}</span></span>
              )}
            </div>
            <p className="text-[11px] text-[#a3a3a3] mt-1" data-testid="pa-sal-total-note">
              Totale lavori incl. extra attivi: <span className="font-medium text-[#737373]">{pct(o.lavori_percentage)}</span> ({money(o.contract_amount)})
            </p>
          </div>
          <div className="text-right text-[11px] text-[#a3a3a3]">Fonte: motore SAL ufficiale<br />(nessun ricalcolo analitico)</div>
        </div>
        <KpiBar value={o.principal_percentage} color="#FF7819" />
      </div>

      {/* 6 card KPI — C/D/E + Extra/Forniture/Altre lavorazioni contrattuali */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pa-cde-cards">
        <CdeCard code="C" cat={C} />
        <CdeCard code="D" cat={D} />
        <CdeCard code="E" cat={E} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pa-secondary-cards">
        <KpiCard title="Extra lavori" color="#6D28D9" pctVal={data.extra.percentage} Icon={Plus} testid="pa-card-extra"
          count={data.extra.item_count}
          rows={[['Eseguito', money(data.extra.executed)], ['Attivo', money(data.extra.contract)]]} />
        <KpiCard title="Forniture" color="#0078C8" pctVal={data.supplies.percentage} Icon={Package} testid="pa-card-supplies"
          count={data.supplies.item_count}
          rows={[['Previsto', money(data.supplies.total_amount)], ['Ordinato', money(data.supplies.ordered_amount)], ['Consegnato', money(data.supplies.delivered_amount)]]} />
        <KpiCard title="Altre lavorazioni contrattuali" color="#737373" pctVal={other ? other.percentage : 0} Icon={Layers} testid="pa-card-other"
          count={other ? other.item_count : 0}
          rows={[['Eseguito', money(other?.executed)], ['Contrattuale', money(other?.contract)], ['Residuo', money(other?.residual)]]} />
      </div>

      {/* Riconciliazione — compatta */}
      {canViewEconomic && (
        reconOk ? (
          <div className="rounded-lg border border-[#E6F7F0] bg-[#F6FFFB] px-4 py-2 flex items-center gap-2 text-[12px] font-medium text-[#0F7A52]" data-testid="pa-reconciliation"
            title={`Δ contratto ${money(recon.CONTRACT_VALUE_RECONCILIATION_DELTA)} · Δ eseguito ${money(recon.EXECUTED_VALUE_RECONCILIATION_DELTA)} · C+D+E+Altre+Extra+Stralciate = SAL ufficiale`}>
            <CheckCircle2 className="w-4 h-4 text-[#00A676]" /> Dati riconciliati
            {recon.needs_classification_review_count > 0 && <span className="ml-auto text-[#B45309] font-normal">{voci(recon.needs_classification_review_count)} da classificare</span>}
          </div>
        ) : (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap text-[12px]" data-testid="pa-reconciliation">
            <span className="flex items-center gap-2 font-semibold text-amber-800"><AlertTriangle className="w-4 h-4" /> Scostamento di riconciliazione</span>
            <span className="text-amber-800">Δ contratto <b>{money(recon.CONTRACT_VALUE_RECONCILIATION_DELTA)}</b> · Δ eseguito <b>{money(recon.EXECUTED_VALUE_RECONCILIATION_DELTA)}</b></span>
          </div>
        )
      )}

      {/* Storico / Da classificare — alert separato, solo se presente */}
      {historical && historical.item_count > 0 && (
        <StoricoAlert historical={historical} canViewEconomic={canViewEconomic} />
      )}

      {/* FILTRI — compatti */}
      <div className="rounded-lg border border-[#EDEDED] bg-white p-2 flex flex-wrap items-center gap-2" data-testid="pa-filters">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-4 h-4 text-[#a3a3a3] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca codice o descrizione"
            className="pl-8 h-8 text-[12px]" data-testid="pa-filter-search" />
        </div>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="h-8 rounded-md border border-[#E5E5E5] text-[12px] px-2 bg-white" data-testid="pa-filter-status">
          <option value="all">Stato</option>
          <option value="non_iniziata">Non iniziate</option>
          <option value="in_corso">In corso</option>
          <option value="completata">Completate</option>
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="h-8 rounded-md border border-[#E5E5E5] text-[12px] px-2 bg-white" data-testid="pa-filter-category">
          <option value="all">Categoria</option>
          <option value="C">C · Opere edili</option>
          <option value="D">D · Impianti elettrici</option>
          <option value="E">E · Idraulici/termici/clima</option>
          <option value="other">Altre lavorazioni</option>
        </select>
        <select value={fSub} onChange={(e) => setFSub(e.target.value)} className="h-8 rounded-md border border-[#E5E5E5] text-[12px] px-2 bg-white max-w-[180px]" data-testid="pa-filter-subcontractor">
          <option value="all">Subappaltatore</option>
          {subOptions.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value="__nonassigned__">Non assegnato</option>
        </select>
        <select value={fAssign} onChange={(e) => setFAssign(e.target.value)} className="h-8 rounded-md border border-[#E5E5E5] text-[12px] px-2 bg-white" data-testid="pa-filter-assignment">
          <option value="all">Assegnazione</option>
          <option value="assigned">Assegnate</option>
          <option value="nonassigned">Non assegnate</option>
        </select>
      </div>

      {/* DETTAGLIO — ordine FISSO: C, D, E, Extra, Forniture, Altre, Stralciate */}
      <div className="space-y-2" data-testid="pa-detail">
        {catFilter('C') && <CategoryAccordion code="C" cat={C} {...detailProps} />}
        {catFilter('D') && <CategoryAccordion code="D" cat={D} {...detailProps} />}
        {catFilter('E') && <CategoryAccordion code="E" cat={E} {...detailProps} />}
        {fCategory === 'all' && <ExtraSection extra={data.extra} canViewEconomic={canViewEconomic} />}
        {fCategory === 'all' && <SuppliesSection supplies={data.supplies} canViewEconomic={canViewEconomic} />}
        {catFilter('other') && <CategoryAccordion code="other" cat={other} {...detailProps} />}

        {/* Voci stralciate */}
        {fCategory === 'all' && data.stricken.item_count > 0 && (
          <div className="rounded-lg border border-[#E5E5E5] bg-white shadow-sm overflow-hidden" data-testid="pa-stricken">
            <button onClick={() => setShowStricken((s) => !s)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAFA]" data-testid="pa-stricken-toggle">
              {showStricken ? <ChevronDown className="w-4 h-4 text-[#a3a3a3]" /> : <ChevronRight className="w-4 h-4 text-[#a3a3a3]" />}
              <Scissors className="w-4 h-4 text-red-500" />
              <span className="font-semibold text-[14px] text-[#171717]">Voci stralciate</span>
              <Badge variant="outline" className="text-[10px]">{data.stricken.item_count}</Badge>
              {canViewEconomic && <span className="hidden md:inline text-[12px] text-[#737373] ml-auto">{money(data.stricken.executed)} eseguito</span>}
            </button>
            {showStricken && (
              <div className="px-3 pb-3 bg-[#FCFCFC] border-t border-[#F0F0F0] pt-2">
                <ItemTable items={data.stricken.items} groupKey="stricken" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressAnalyticsDashboard;
